import { notification } from 'antd'
import { SlamFloorplanCanvas } from './SlamFloorplanCanvas'
import type { Position, SlamJson, SlamPoint } from '../types'
import { goHome, moveRobot } from '@/api/robots.api'
import { EmptyState } from '../../components/EmptyState'
import { Button } from '@/components/ui/button'

interface PositionSetupCanvasPanelProps {
  hasMap: boolean
  hasSlamLoadError?: boolean
  isPositionSetupLoading: boolean
  showPositionSetupContent: boolean
  slamJson: SlamJson
  selectedCoordinate: { x: number; y: number } | null
  markers: Array<Position & { selected?: boolean }>
  pendingMarker: SlamPoint | null
  selectedMarkerId: string | null
  rotateMode: boolean
  rotateMarkerId: string | null
  isConfirmingAddNew: boolean
  directionOverlayMarkerId: string | null
  directionOverlayYaw: number | null
  isAddmode: boolean
  homePlacementLock?: boolean
  homePlacementPoint?: SlamPoint | null
  isEditable?: boolean
  robotId: string
  isRetrying?: boolean
  retrySeconds?: number
  mapRotation?: number
  backgroundImageUrl?: string | null
  backgroundWidthM?: number
  backgroundHeightM?: number

  onSlamClick: (point: SlamPoint) => void
  onMarkerClick: (positionId: string) => void
  onMarkerEditDirection: (positionId: string) => void
  onMarkerYawChange: (positionId: string, yaw: number) => void
  onMarkerYawCommit: (positionId: string, yaw: number) => void
  onConfirmAddNew: () => void | Promise<void>
  onRetry?: () => void
  onRepickHome?: () => void
}

export function PositionSetupCanvasPanel({
  hasMap,
  hasSlamLoadError,
  showPositionSetupContent,
  slamJson,
  selectedCoordinate,
  markers,
  pendingMarker,
  selectedMarkerId,
  rotateMode,
  rotateMarkerId,
  isConfirmingAddNew,
  directionOverlayMarkerId,
  directionOverlayYaw,
  isEditable,
  robotId,
  isRetrying,
  retrySeconds,
  mapRotation,
  backgroundImageUrl,
  backgroundWidthM,
  backgroundHeightM,
  isAddmode = false,
  homePlacementLock = false,
  homePlacementPoint = null,
  onSlamClick,
  onMarkerClick,
  onMarkerEditDirection,
  onMarkerYawChange,
  onMarkerYawCommit,
  onConfirmAddNew,
  onRetry,
  onRepickHome,
}: PositionSetupCanvasPanelProps) {
  const handleMoveRobot = async (yaw: number) => {
    const targetX = selectedCoordinate?.x ?? 0
    const targetY = selectedCoordinate?.y ?? 0
    try {
      const response = await moveRobot(robotId, {
        x: +targetX.toFixed(5),
        y: +targetY.toFixed(5),
        yaw: yaw !== 0 ? +yaw.toFixed(5) : 0,
      })
      if (response.ok) {
        notification.success({ title: 'Robot đã di chuyển thành công.' })
      }
    } catch (error) {
      notification.error({
        title: 'Không thể di chuyển robot. Vui lòng thử lại.',
      })
    }
  }

  const handleGoHome = async () => {
    try {
      const response = await goHome(robotId)
      if (response.ok) {
        notification.success({
          title: 'Robot đã trở về trụ sạc thành công.',
        })
      }
    } catch (error) {
      notification.error({
        title: 'Không thể đưa robot về trụ sạc. Vui lòng thử lại.',
      })
    }
  }

  if (!showPositionSetupContent) {
    if (!hasMap) {
      return (
        <EmptyState
          title="Chưa có dữ liệu sơ đồ sàn."
          desc="Robot chưa thực hiện quét bản đồ. Vui lòng vận hành robot quét sàn (SLAM) để tạo sơ đồ trước khi thiết lập tọa độ vị trí."
          action="Thử lại"
          onAction={onRetry}
          isRetrying={isRetrying}
          seconds={retrySeconds}
        />
      )
    }

    if (hasSlamLoadError) {
      return (
        <EmptyState
          title="Tải sơ đồ sàn không thành công."
          desc="Hệ thống không thể hiển thị sơ đồ sàn hiện tại của robot. Vui lòng làm mới lại sơ đồ."
          action="Thử lại"
          onAction={onRetry}
          isRetrying={isRetrying}
          seconds={retrySeconds}
        />
      )
    }

    // Trạng thái loading mặc định
    return (
      <div className="flex min-h-[560px] w-full flex-col items-center justify-center rounded-xl border border-gray-200 bg-white">
        <div className="mx-auto mb-3 h-9 w-9 animate-spin rounded-full border-2 border-gray-200 border-t-[#CE7A58]" />
        <p className="text-sm font-medium text-[#6b7280]">
          Đang tải dữ liệu...
        </p>
      </div>
    )
  }

  return (
    <div className="flex min-w-0 flex-1 flex-col rounded-2xl border border-gray-200/80 bg-white/95 p-4 shadow-[0_12px_40px_rgba(26,26,26,0.05)] backdrop-blur-sm">
      <div className="flex w-full flex-col gap-3 px-1 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1">
          <div className="text-lg font-bold tracking-tight text-[#1a1a1a]">
            Sơ đồ
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!homePlacementLock && onRepickHome && (
            <Button
              variant="outline"
              size="md"
              className="w-full gap-2 border border-[#A85D3D] text-[#A85D3D] hover:!bg-[#CE7A58] hover:!text-[#fef3f0] sm:w-auto"
              onClick={onRepickHome}
            >
              Chấm lại tọa độ Home
            </Button>
          )}
          {!isRetrying ? (
            <Button
              variant="outline"
              size="md"
              className="w-full gap-2 border border-[#A85D3D] text-[#A85D3D] hover:!bg-[#CE7A58] hover:!text-[#fef3f0] sm:w-auto"
              onClick={onRetry}
            >
              Làm mới
            </Button>
          ) : (
            <p className="text-sm font-medium text-[#CE7A58] animate-pulse">
              Đang gửi yêu cầu và đợi sơ đồ mới ({retrySeconds}s)...
            </p>
          )}
        </div>
      </div>
      <div
        className="floorplan-canvas relative z-10 min-h-[min(62vh,820px)] flex-1 overflow-hidden rounded-2xl border border-gray-200/80 bg-white"
        role="button"
        tabIndex={0}
        aria-label="Khu vực chấm tọa độ trên sơ đồ sàn"
      >
        <SlamFloorplanCanvas
          slamJson={slamJson}
          markers={markers}
          pendingMarker={pendingMarker}
          selectedMarkerId={selectedMarkerId}
          rotateMode={rotateMode}
          rotateMarkerId={rotateMarkerId}
          isConfirmingAddNew={isConfirmingAddNew}
          directionOverlayMarkerId={directionOverlayMarkerId}
          directionOverlayYaw={directionOverlayYaw}
          isEditable={isEditable}
          mapRotation={mapRotation}
          backgroundImageUrl={backgroundImageUrl}
          backgroundWidthM={backgroundWidthM}
          backgroundHeightM={backgroundHeightM}
          isAddmode={isAddmode}
          homePlacementLock={homePlacementLock}
          homePlacementPoint={homePlacementPoint}
          onSlamClick={onSlamClick}
          onMarkerClick={onMarkerClick}
          onMarkerEditDirection={onMarkerEditDirection}
          onMarkerYawChange={onMarkerYawChange}
          onMarkerYawCommit={onMarkerYawCommit}
          onConfirmAddNew={onConfirmAddNew}
          onMoveRobot={handleMoveRobot}
          onGoHome={handleGoHome}
        />
      </div>
    </div>
  )
}
